import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Edit3, 
  Eye, 
  Star, 
  Search,
  Grid3X3,
  List,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { GalleryImage, GalleryResponse } from '../types';

interface GalleryFilters {
  category: string;
  featured: boolean | null;
  search: string;
}

const Gallery: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const [editingImage, setEditingImage] = useState<GalleryImage | null>(null);
  const [previewImage, setPreviewImage] = useState<GalleryImage | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [services, setServices] = useState<{ id: number; title: string; color_gradient?: string }[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState<GalleryFilters>({
    category: '',
    featured: null,
    search: ''
  });
  const [uploadCategory, setUploadCategory] = useState<string>('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });

  const loadImages = useCallback(async () => {
    try {
      setLoading(true);
      const response: GalleryResponse = await apiService.getGalleryImages(
        pagination.current_page,
        pagination.items_per_page,
        filters.category || undefined,
        filters.featured || undefined
      );
      
      // Filter by search term if provided
      let filteredImages = response.images;
      if (filters.search) {
        filteredImages = response.images.filter(img => 
          img.caption?.toLowerCase().includes(filters.search.toLowerCase()) ||
          img.original_filename.toLowerCase().includes(filters.search.toLowerCase()) ||
          img.category?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setImages(filteredImages);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast.error('Failed to load gallery images');
    } finally {
      setLoading(false);
    }
  }, [pagination.current_page, pagination.items_per_page, filters]);

  const loadCategories = useCallback(async () => {
    try {
      const [cats, svcs] = await Promise.all([
        apiService.getImageCategories(),
        apiService.getServicesForCategories()
      ]);
      setCategories(cats);
      setServices(svcs);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, []);

  // Load images on component mount
  useEffect(() => {
    loadImages();
    loadCategories();
  }, [loadImages, loadCategories]);

  // Dropzone configuration
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    // Upload files with selected category/service
    const uploadPromises = acceptedFiles.map(async (file) => {
      try {
        const selectedService = services.find(s => s.title === uploadCategory);
        const uploadedImage = await apiService.uploadImage(
          file, 
          '', // caption - can be edited later
          uploadCategory || undefined, // category
          false, // featured
          selectedService?.id // service_id
        );
        toast.success(`Uploaded: ${file.name}${uploadCategory ? ` to ${uploadCategory}` : ''}`);
        return uploadedImage;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`Failed to upload: ${file.name}`);
        return null;
      }
    });

    setUploading(true);
    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter((result): result is GalleryImage => result !== null);
    
    if (successfulUploads.length > 0) {
      setImages(prev => [...successfulUploads, ...prev]);
      setPagination(prev => ({
        ...prev,
        total_items: prev.total_items + successfulUploads.length
      }));
    }
    
    setUploading(false);
  }, [uploadCategory, services]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true
  });

  // Image operations
  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await apiService.deleteImage(imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
      setSelectedImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error('Failed to delete image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleUpdateImage = async (imageId: number, data: Partial<GalleryImage>) => {
    try {
      const updatedImage = await apiService.updateImage(imageId, data);
      setImages(prev => prev.map(img => 
        img.id === imageId ? updatedImage : img
      ));
      setEditingImage(null);
      toast.success('Image updated successfully');
    } catch (error) {
      console.error('Failed to update image:', error);
      toast.error('Failed to update image');
    }
  };

  const handleToggleFeatured = async (imageId: number, featured: boolean) => {
    try {
      await apiService.updateImage(imageId, { featured });
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, featured } : img
      ));
      toast.success(featured ? 'Image marked as featured' : 'Image unmarked as featured');
    } catch (error) {
      console.error('Failed to toggle featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedImages.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedImages.size} images?`)) return;

    try {
      const deletePromises = Array.from(selectedImages).map(id => 
        apiService.deleteImage(id)
      );
      await Promise.all(deletePromises);
      
      setImages(prev => prev.filter(img => !selectedImages.has(img.id)));
      setSelectedImages(new Set());
      toast.success(`Deleted ${selectedImages.size} images successfully`);
    } catch (error) {
      console.error('Failed to delete images:', error);
      toast.error('Failed to delete some images');
    }
  };

  const toggleImageSelection = (imageId: number) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const selectAllImages = () => {
    setSelectedImages(new Set(images.map(img => img.id)));
  };

  const clearSelection = () => {
    setSelectedImages(new Set());
  };

  // Render functions
  const renderImageCard = (image: GalleryImage) => {
    const isSelected = selectedImages.has(image.id);
    const imageUrl = apiService.getImageUrl(`uploads/gallery/${image.filename}`);

    return (
      <motion.div
        key={image.id}
        layout
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className={`relative group cursor-pointer ${
          viewMode === 'grid' ? 'col-span-1' : 'col-span-full'
        }`}
      >
        <div className={`
          relative overflow-hidden rounded-lg border-2 transition-all duration-200
          ${isSelected 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-200 hover:border-gray-300'
          }
          ${viewMode === 'grid' ? 'aspect-square' : 'h-24'}
        `}>
          {/* Selection checkbox */}
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleImageSelection(image.id)}
              className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500"
            />
          </div>

          {/* Featured badge */}
          {image.featured && (
            <div className="absolute top-2 right-2 z-10">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
            </div>
          )}

          {/* Image */}
          <img
            src={imageUrl}
            alt={image.caption || image.original_filename}
            className="w-full h-full object-cover"
            onClick={() => setPreviewImage(image)}
          />

          {/* Overlay with actions */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage(image);
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100"
              >
                <Eye className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingImage(image);
                }}
                className="p-2 bg-white rounded-full hover:bg-gray-100"
              >
                <Edit3 className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFeatured(image.id, !image.featured);
                }}
                className={`p-2 rounded-full ${
                  image.featured 
                    ? 'bg-yellow-500 hover:bg-yellow-600' 
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                <Star className={`w-4 h-4 ${image.featured ? 'text-white' : 'text-gray-700'}`} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteImage(image.id);
                }}
                className="p-2 bg-red-500 rounded-full hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Image info */}
        <div className="mt-2 space-y-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {image.caption || image.original_filename}
          </p>
          {image.category && (
            <p className="text-xs text-gray-500">{image.category}</p>
          )}
          <p className="text-xs text-gray-400">
            {new Date(image.created_at).toLocaleDateString()}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gallery Management</h1>
          <p className="text-gray-600">Manage your gallery images, upload new ones, and organize your content.</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            {viewMode === 'grid' ? <List className="w-5 h-5" /> : <Grid3X3 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Service Category Selection for Upload */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Service Category for Upload (Optional)
            </label>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">No Category</option>
              {services.map(service => (
                <option key={service.id} value={service.title}>
                  {service.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Images will be automatically categorized under the selected service. You can change this later by editing individual images.
            </p>
          </div>
          {uploadCategory && (
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
              <span className="font-medium">Uploading to:</span><br />
              <span className="text-primary-600">{uploadCategory}</span>
            </div>
          )}
        </div>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer
          ${isDragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${uploading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <p className="text-lg font-medium text-gray-900">
            {isDragActive ? 'Drop images here' : 'Drag & drop images here'}
          </p>
          <p className="text-gray-500">or click to select files</p>
          <p className="text-sm text-gray-400 mt-2">
            Supports: JPG, PNG, GIF, WebP (max 10MB each)
          </p>
        </div>
        {uploading && (
          <div className="mt-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Uploading...</p>
          </div>
        )}
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search images..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category filter */}
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Featured filter */}
          <select
            value={filters.featured === null ? '' : filters.featured.toString()}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              featured: e.target.value === '' ? null : e.target.value === 'true'
            }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Images</option>
            <option value="true">Featured Only</option>
            <option value="false">Not Featured</option>
          </select>
        </div>

        {/* Batch actions */}
        {selectedImages.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedImages.size} selected
            </span>
            <button
              onClick={handleBatchDelete}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Delete Selected
            </button>
            <button
              onClick={clearSelection}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Selection actions */}
      {images.length > 0 && (
        <div className="flex items-center gap-4 text-sm">
          <button
            onClick={selectAllImages}
            className="text-primary-600 hover:text-primary-700"
          >
            Select All
          </button>
          <button
            onClick={clearSelection}
            className="text-gray-600 hover:text-gray-700"
          >
            Clear Selection
          </button>
          <span className="text-gray-500">
            {images.length} images total
          </span>
        </div>
      )}

      {/* Images Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-12">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No images</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by uploading some images to your gallery.
          </p>
        </div>
      ) : (
        <div className={`
          grid gap-4
          ${viewMode === 'grid' 
            ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6' 
            : 'grid-cols-1'
          }
        `}>
          <AnimatePresence>
            {images.map(renderImageCard)}
          </AnimatePresence>
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={pagination.current_page === 1}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={pagination.current_page === pagination.total_pages}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Image Modal */}
      {editingImage && (
        <EditImageModal
          image={editingImage}
          categories={categories}
          services={services}
          onSave={handleUpdateImage}
          onCancel={() => setEditingImage(null)}
        />
      )}

      {/* Preview Image Modal */}
      {previewImage && (
        <PreviewImageModal
          image={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

// Edit Image Modal Component
interface EditImageModalProps {
  image: GalleryImage;
  categories: string[];
  services: { id: number; title: string; color_gradient?: string }[];
  onSave: (id: number, data: Partial<GalleryImage>) => Promise<void>;
  onCancel: () => void;
}

const EditImageModal: React.FC<EditImageModalProps> = ({ image, categories, services, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    caption: image.caption || '',
    category: image.category || '',
    service_id: (image as any).service_id || '',
    featured: image.featured
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(image.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Edit Image</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caption
            </label>
            <input
              type="text"
              value={formData.caption}
              onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              maxLength={200}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Category
            </label>
            <select
              value={formData.service_id}
              onChange={(e) => {
                const selectedServiceId = e.target.value;
                const selectedService = services.find(s => s.id.toString() === selectedServiceId);
                setFormData(prev => ({ 
                  ...prev, 
                  service_id: selectedServiceId,
                  category: selectedService?.title || ''
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">No Category</option>
              {services.map(service => (
                <option key={service.id} value={service.id.toString()}>
                  {service.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select a service to categorize this image, or leave empty for no category
            </p>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="featured" className="ml-2 text-sm text-gray-700">
              Mark as featured
            </label>
          </div>
          
          <div className="flex gap-2 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Save Changes
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Preview Image Modal Component
interface PreviewImageModalProps {
  image: GalleryImage;
  onClose: () => void;
}

const PreviewImageModal: React.FC<PreviewImageModalProps> = ({ image, onClose }) => {
  const imageUrl = apiService.getImageUrl(`uploads/gallery/${image.filename}`);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-4xl max-h-full">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
        >
          <X className="w-6 h-6" />
        </button>
        
        <img
          src={imageUrl}
          alt={image.caption || image.original_filename}
          className="max-w-full max-h-full object-contain"
        />
        
        <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
          <h3 className="font-medium">{image.caption || image.original_filename}</h3>
          {image.category && (
            <p className="text-sm opacity-75">{image.category}</p>
          )}
          <p className="text-sm opacity-75">
            Uploaded: {new Date(image.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Gallery; 